import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

import { Edit3, AlertCircle } from "lucide-react";
import { formatCurrency } from "@/lib/formatters";

interface ProfileClient {
  id: number;
  codigo_cnx: string | null;
  nome_cliente: string | null;
  codigo_produto: string | null;
  descricao_produto: string | null;
  utilizacao: string | null;
  ano_letivo: number | null;
  serie: string | null;
  segmento: string | null;
  quantidade_contratual: number | null;
  desconto: number | null;
  preco: number | null;
  habilitado: boolean | null;
  comentario?: string | null;
}

interface EditProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: ProfileClient) => void;
  product: ProfileClient;
  existingProductCodes: (string | null)[];
}

export const EditProductDialog = ({
  isOpen,
  onClose,
  onSave,
  product,
  existingProductCodes
}: EditProductDialogProps) => {
  const [quantidade, setQuantidade] = useState<string>("1");
  const [preco, setPreco] = useState<string>("");
  const [comentario, setComentario] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [originalValues, setOriginalValues] = useState<{
    quantidadeContratual: number | null;
    preco: number | null;
  }>({ quantidadeContratual: null, preco: null });
  const [errors, setErrors] = useState({ quantidade: '', comentario: '' });

  useEffect(() => {
    if (isOpen && product) {
      setQuantidade(product.quantidade_contratual?.toString() || "");
      const precoFormatado = (product.preco || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      setPreco(precoFormatado);
      setComentario("");
      setOriginalValues({
        quantidadeContratual: product.quantidade_contratual,
        preco: product.preco,
      });
      setError("");
    }
  }, [isOpen, product]);

  const parseCurrencyInput = (masked: string): number => {
    const digits = masked.replace(/[^\d]/g, "");
    if (!digits) return 0;
    return Number(digits) / 100;
  };

  const formatCurrencyMask = (raw: string): string => {
    const digits = raw.replace(/[^\d]/g, "");
    if (!digits) return "";
    const num = Number(digits) / 100;
    return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Detectar se houve alterações nos campos
  const hasChanges = () => {
    const currentQuantidade = parseInt(quantidade) || null;
    const currentPreco = parseCurrencyInput(preco);

    return (
      currentQuantidade !== originalValues.quantidadeContratual ||
      currentPreco !== originalValues.preco
    );
  };

  const isComentarioRequired = hasChanges();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!quantidade || parseInt(quantidade) <= 0) {
      setError("Quantidade contratual deve ser maior que zero");
      return;
    }

    // Validar comentário se houver alterações
    if (isComentarioRequired && !comentario.trim()) {
      setError("Comentário é obrigatório quando há alterações nos campos");
      return;
    }

    if (comentario.length > 250) {
      setError("Comentário deve ter no máximo 250 caracteres");
      return;
    }

    const precoNum = parseCurrencyInput(preco);
    const updatedProduct: ProfileClient = {
      ...product,
      quantidade_contratual: parseInt(quantidade),
      desconto: 0,
      preco: precoNum,
      comentario: comentario.trim() || null,
      habilitado: true,
    };

    onSave(updatedProduct);
    handleClose();
  };

  const handleClose = () => {
    setError("");
    setComentario("");
    onClose();
  };

  const isCommentRequired = hasChanges;

  const totalValue = parseCurrencyInput(preco) * parseInt(quantidade || "0");

    const validate = useCallback(() => {
      const newErrors = { quantidade: '', comentario: '' };

      const qtd = parseInt(quantidade, 10);
      if (isNaN(qtd) || !Number.isInteger(Number(quantidade)) || qtd < 0) {
        newErrors.quantidade = "Deve ser um número inteiro maior ou igual a 0.";
      }

      if (qtd > 10000) {
        newErrors.quantidade = "Deve ser um número entre 0 e 10000.";
        setQuantidade('10000');
      }

      if (isCommentRequired && comentario.trim().length < 5) {
        newErrors.comentario = "Mínimo de 5 caracteres.";
      }

      setErrors(newErrors);
      return Object.values(newErrors).every(x => x === "");
    }, [quantidade, comentario, isCommentRequired]);

    useEffect(() => {
      validate();
    }, [quantidade, comentario, validate]);

    const isFormValid = Object.values(errors).every(x => x === "");

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-primary" />
            Editar Produto
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label>Cliente</Label>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{product.codigo_cnx}</Badge>
              <span className="font-medium">{product.nome_cliente}</span>
            </div>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Badge variant="outline">{product.codigo_produto}</Badge>
              {product.descricao_produto}
            </h4>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Utilização:</span>
                <span className="ml-2">{product.utilizacao || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Ano Letivo:</span>
                <span className="ml-2">{product.ano_letivo || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Série:</span>
                <span className="ml-2">{product.serie || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Segmento:</span>
                <span className="ml-2">{product.segmento || "N/A"}</span>
              </div>
            </div>

            {product.preco && (
              <div className="text-sm">
                <span className="text-muted-foreground">Valor unitário:</span>
                <span className="ml-2 font-semibold">
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(product.preco)}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="preco">Valor (R$) *</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">R$</span>
                <Input
                  id="preco"
                  type="text"
                  inputMode="numeric"
                  placeholder="0,00"
                  value={preco}
                  onChange={(e) => setPreco(formatCurrencyMask(e.target.value))}
                  className="text-xs h-8 pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quantidade">Quantidade Referência *</Label>
              <Input
                  type="number"
                  min="0"
                  step="1"
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Qtd"
                  className={`text-xs h-8 ${errors.quantidade ? 'border-destructive' : ''}`}
                />
                {errors.quantidade && <p className="text-xs text-destructive mt-1">{errors.quantidade}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comentario" className="flex items-center gap-1">
              Comentário
              {isComentarioRequired && (
                <span className="text-destructive">*</span>
              )}
            </Label>

            <Textarea
                id={`comentario-${product.codigo_produto}`}
                value={comentario}
                onChange={(e) => setComentario(e.target.value.slice(0, 250))}
                placeholder={isCommentRequired ? "Comentário obrigatório ao alterar valores" : "Comentário opcional"}
                className={`text-xs min-h-[60px] resize-none ${errors.comentario ? 'border-destructive' : ''}`}
                maxLength={250}
              />
              {errors.comentario && <p className="text-xs text-destructive mt-1">{errors.comentario}</p>}


            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {isComentarioRequired
                  ? "Campo obrigatório quando há alterações"
                  : "Máximo 250 caracteres"}
              </span>
              <span
                className={comentario.length > 240 ? "text-destructive" : ""}
              >
                {comentario.length}/250
              </span>
            </div>
          </div>

          {quantidade && product.preco && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">ACV:</span>
                <span className="text-xl font-bold text-primary">
                  {formatCurrency(totalValue)}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-destructive text-sm">{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                !isFormValid || !quantidade ||
                (isComentarioRequired && !comentario.trim())
              }
            >
              Salvar Alterações
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
